import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, CircleNotch, UploadSimple, VideoCamera } from "@phosphor-icons/react";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { uploadMedia, mediaSrc } from "./serviceApi";

// Poll a media item while `active(m)` is true.
export function useMediaPoll(client, media, onUpdate, active) {
  const ref = useRef(onUpdate);
  ref.current = onUpdate;
  const on = !!media && active(media);
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(async () => {
      try { ref.current(await client.getMedia(media.id)); } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [on, media?.id, client]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Upload a video into the unsaved entry (draft), transcribe + summarize, and
 *  push the reviewed summary into the form's notes without leaving the form. */
export function TranscribeUploadModal({ open, onOpenChange, client, target, context, notes, onApplyNotes, onUploaded }) {
  const [media, setMedia] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState("");
  const [confirmReplace, setConfirmReplace] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setMedia(null); setSummary(""); setProgress(0); } }, [open]);
  useMediaPoll(client, media, (m) => { setMedia(m); if (m.transcription_status === "done") setSummary(m.ai_summary || ""); },
    (m) => m.transcription_status === "processing" || m.processing_status === "processing");

  const pick = async (file) => {
    if (!file) return;
    setUploading(true); setProgress(0);
    try {
      const m = await uploadMedia(client, target, file, setProgress);
      setMedia(m); onUploaded?.(m);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed — try again"));
    } finally { setUploading(false); }
  };
  const transcribe = async () => {
    try { await client.transcribe(media.id, context); setMedia({ ...media, transcription_status: "processing" }); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not start transcription")); }
  };
  const apply = (mode) => {
    onApplyNotes(summary.trim(), mode, media);
    toast.success(mode === "replace" ? "Service notes replaced" : "Added to service notes");
  };
  const status = media?.transcription_status;

  return (
    <Dialog open={open} onOpenChange={(v) => !uploading && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="transcribe-upload-modal">
        <DialogHeader><DialogTitle>Upload & Transcribe video</DialogTitle></DialogHeader>
        {!media && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="w-full rounded-lg border-2 border-dashed border-slate-300 hover:border-[#327de3] px-4 py-8 text-center transition-colors" data-testid="tu-pick-btn">
            <input ref={inputRef} type="file" accept="video/*,.mov,.mp4" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="tu-file-input" />
            {uploading ? (
              <div className="space-y-2"><div className="text-sm text-slate-600">Uploading… {progress}%</div><Progress value={progress} className="h-1.5" /></div>
            ) : (
              <><UploadSimple size={24} className="mx-auto text-[#327de3] mb-1" /><div className="text-sm font-semibold text-slate-700">Choose or record a video</div></>
            )}
          </button>
        )}
        {media && (
          <div className="space-y-3">
            <video src={mediaSrc(media.display_url)} controls playsInline preload="metadata" className="w-full max-h-[40vh] bg-black rounded-lg" data-testid="tu-preview" />
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={transcribe} disabled={status === "processing"} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="tu-transcribe-btn">
                {status === "processing" ? <CircleNotch size={14} className="mr-1.5 animate-spin" /> : <Sparkle size={14} weight="fill" className="mr-1.5" />}
                {status === "processing" ? "Transcribing…" : "Transcribe & Summarize"}
              </Button>
              <span className="text-[11px] text-slate-500 inline-flex items-center gap-1"><VideoCamera size={12} /> {media.filename} · attached to this entry when you save</span>
            </div>
            {(status === "failed" || status === "no_speech") && <div className="text-sm text-rose-600" data-testid="tu-error">{media.transcription_error || "No speech detected."}</div>}
            {media.transcript && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Full transcript</div>
                <div className="text-sm text-slate-700 bg-slate-50 rounded-md p-2.5 max-h-36 overflow-y-auto whitespace-pre-wrap" data-testid="tu-transcript">{media.transcript}</div>
              </div>
            )}
            {status === "done" && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-600"><Sparkle size={12} weight="fill" /> AI-generated summary — edit before adding</div>
                <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="tu-summary-input" />
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" onClick={() => apply("append")} disabled={!summary.trim()} className="bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="tu-add-notes-btn">Add to Service Notes</Button>
                  <Button size="sm" variant="outline" onClick={() => (notes?.trim() ? setConfirmReplace(true) : apply("replace"))} disabled={!summary.trim()} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="tu-replace-notes-btn">Replace Service Notes</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {media && <Button variant="outline" onClick={() => { setMedia(null); setSummary(""); }} data-testid="tu-another-btn">Upload another video</Button>}
          <Button onClick={() => onOpenChange(false)} disabled={uploading} data-testid="tu-done-btn">Done</Button>
        </DialogFooter>
        <AlertDialog open={confirmReplace} onOpenChange={setConfirmReplace}>
          <AlertDialogContent data-testid="tu-replace-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Replace existing service notes?</AlertDialogTitle>
              <AlertDialogDescription>The notes you've typed will be overwritten by this summary.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tu-replace-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setConfirmReplace(false); apply("replace"); }} className="bg-rose-600 hover:bg-rose-700" data-testid="tu-replace-ok">Replace</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
